import { useDispatch, useSelector } from 'react-redux';
import { alertActions, conversationsActions, inboxActions } from '@actions';
import { useConversations, deriveConversationRead } from './useConversations';
import { userCoversTeam } from './useInboxTeams';
import { useTranslation } from './useTranslation';

// Single aftermath for a conversation changing teams, shared by the actor (handover
// modal) and SignalR recipients: clients that keep access update in place and pull
// the TeamChanged activity into the thread; clients that lose access drop the
// conversation. `isActive` marks whether this client has the conversation open.
export const useTeamMove = ({ setActiveConversation, filterRef }) => {
    const currentUser = useSelector(state => state.auth.user);

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { upsertConversationInFeed, removeConversationById } = useConversations();

    return (conversation, { team = null, isActor = false, isActive = true } = {}) => {
        const teamName = team?.name || conversation?.teamName;

        // A collaborator grant keeps the conversation visible across team moves, including the actor who opted to stay.
        const isCollaborator = (conversation.collaborators || []).includes(currentUser?.id);

        if (!userCoversTeam(currentUser, conversation.teamId) && !isCollaborator) {
            removeConversationById(conversation.id);
            dispatch(conversationsActions.getCounters(filterRef.current));
            if (isActive) {
                setActiveConversation(null);
                dispatch(alertActions.success(t('InboxPage.movedToTeam', { team: teamName })));
            }
            return;
        }

        // The broadcast is marked unread for the target queue; a client that is viewing
        // the conversation (the actor included, via the echo) keeps it read
        const read = isActive || deriveConversationRead(conversation, currentUser?.id);
        const resolvedConversation = { ...conversation, read };

        if (isActive) {
            setActiveConversation(prev => (prev?.id === conversation.id ? { ...prev, ...resolvedConversation } : prev));
            dispatch(inboxActions.getHistory(conversation.id, false));
        }

        upsertConversationInFeed(resolvedConversation, filterRef.current);

        if (isActor) {
            dispatch(alertActions.success(t('InboxPage.movedToTeam', { team: teamName })));
        }
    };
};
